import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

import {
  DataBlocks,
  Table,
  I18n,
  Link,
  RouterLink,
  Button,
} from 'components';

import {
  formatDateString,
  formatUnitString,
  formatPhone,
} from 'utils/formatString';
import { encryptText } from 'utils/encryption';

import { UI_DATE_FORMAT } from '../../constants';

import styles from './styles.pcss';
const cx = classNames.bind(styles);

const renderCostsBlock = (banNrtbcBalance, currency) => (
  <div className={cx('spendingLimit')}>
    {banNrtbcBalance ? (
      <>
        <I18n path="detalization.postpaidInvoices.spendingLimit" />
          &nbsp;
        {formatUnitString(banNrtbcBalance, currency)}
        <br />
        <RouterLink
          className="link--hidden-visited-color"
          to="/settings/number-management"
        >
          <I18n path="detalization.postpaidInvoices.configure" />
        </RouterLink>
      </>
    ) : (
      <>
        {banNrtbcBalance === 0 && (
        <>
          <I18n path="detalization.postpaidInvoices.limitIsUnspecified" />
          <br />
          <RouterLink
            className="link--hidden-visited-color"
            to="/settings/number-management"
          >
            <I18n path="detalization.postpaidInvoices.configure" />
          </RouterLink>
        </>
        )}
      </>
    )}
  </div>
);

const CurrentPeriodExpenses = ({ period, currency, onCtnClick, showCostsBlock = true }) => {
  if (!period || !period.bens) return null;

  return (
    <DataBlocks.ContentGroup
      title={(
        <I18n
          path="detalization.postpaidInvoices.currentPeriod"
          vars={{
            start: formatDateString(period.startDate, UI_DATE_FORMAT),
            end: formatDateString(new Date(), UI_DATE_FORMAT),
          }}
        />
      )}
    >
      <div className={cx('totalContainer')}>
        <div className={cx('totalField')}>
          {formatUnitString(period.totalAmount, currency)}
        </div>

        {showCostsBlock && renderCostsBlock(period.banNrtbcBalance, currency)}

        {period.bens.length === 1 && (
          <div className={cx('detalizationButton')}>
            <Button
              onClick={() => onCtnClick({ ctnFor: encryptText(period.bens[0].ctn) })}
            >
              <I18n path="detalization.postpaidInvoices.seeDetalization" />
            </Button>
          </div>
        )}
      </div>
      {period.bens.length > 1 && (
        <Table
          data={period.bens}
          config={{
            columns: [
              {
                name: 'detalization.postpaidInvoices.group',
                dataField: 'benNumber',
                hidden: period.bens.length < 2,
                transformer: ({ benNumber }) => (
                  <I18n
                    path="detalization.postpaidInvoices.accountGroup"
                    vars={{
                      group: benNumber,
                    }}
                  />
                ),
                hideOnMobile: true,
              },
              {
                name: 'detalization.postpaidInvoices.phoneNumber',
                dataField: 'ctn',
                transformer: ({ ctn, ctnNickname = '' }) => (
                  <>
                    <Link
                      onClick={() => {
                        window.scrollTo(0, 0);
                        onCtnClick({ ctnFor: encryptText(ctn) });
                      }}
                    >
                      {formatPhone(ctn)}
                    </Link>
                    <br />
                    {ctnNickname}
                  </>
                ),
              },
              {
                name: 'detalization.postpaidInvoices.preliminaryAmount',
                dataField: 'amount',
                transformer: ({ amount }) => formatUnitString(amount, currency),
              },
              {
                name: 'detalization.postpaidInvoices.spendingLimit',
                dataField: 'nrtbcBalance',
                transformer: ({ nrtbcBalance }) => {
                  if (nrtbcBalance) {
                    return (
                      <>
                        {formatUnitString(nrtbcBalance, currency)}
                        <br />
                        <RouterLink
                          className="link--hidden-visited-color"
                          to="/settings/number-management"
                        >
                          <I18n path="detalization.postpaidInvoices.configure" />
                        </RouterLink>
                      </>
                    );
                  } if (nrtbcBalance === 0) {
                    return (
                      <>
                        <I18n path="detalization.postpaidInvoices.limitIsUnspecified" />
                        <br />
                        <RouterLink
                          className="link--hidden-visited-color"
                          to="/settings/number-management"
                        >
                          <I18n path="detalization.postpaidInvoices.configure" />
                        </RouterLink>
                      </>
                    );
                  }
                  return null;
                },
                hideOnMobile: true,
              },
            ],
            uniqueIdField: 'ctn',
          }}
        />
      )}
    </DataBlocks.ContentGroup>
  );
};

CurrentPeriodExpenses.propTypes = {
  period: PropTypes.shape({
    startDate: PropTypes.any,
    totalAmount: PropTypes.number,
    banNrtbcBalance: PropTypes.number,
    bens: PropTypes.array,
  }),
  currency: PropTypes.string,
  onCtnClick: PropTypes.func,
  showCostsBlock: PropTypes.bool,
};

export default CurrentPeriodExpenses;
